import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { errors, paxRoute, Sha256, Timestamp } from "../../common";
import { automodUnavailable, surfaceBanned } from "../../moderation/v1alpha1/endpoints";
import { UploadId } from "../../profiles/v1alpha1/schemas";
import { DraftParams } from "./authoring";
import { teamRefusals } from "./refusals";
import { ArtworkTint } from "./schemas";

/** What a preset's artwork can be: a cover, a landing still, animation, or video, or a map still. */
export const ARTWORK_KINDS = ["cover", "landing", "map"] as const;
export const ArtworkKind = z.enum(ARTWORK_KINDS).meta({
  id: "ArtworkKind",
  description:
    "`cover`: the picture on the preset's card. `landing`: the hero artwork, a still, an animation, or a video. `map`: a still of the opening map.",
});
export type ArtworkKind = z.infer<typeof ArtworkKind>;

export const ARTWORK_UPLOAD_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "video/mp4",
] as const;

/** The largest artwork upload: a landing video. */
export const ARTWORK_UPLOAD_MAX_BYTES = 64 * 1024 * 1024;

export const CreateArtworkUploadRequest = z
  .strictObject({
    contentType: z.enum(ARTWORK_UPLOAD_CONTENT_TYPES).meta({
      description: "The file's type. Only a landing takes a video.",
      example: "image/jpeg",
    }),
    bytes: z.int().min(1).max(ARTWORK_UPLOAD_MAX_BYTES).meta({
      description:
        "The file's size in bytes. The upload URL accepts only a file of exactly this size.",
      example: 1_482_113,
    }),
  })
  .meta({ id: "CreateArtworkUploadRequest", description: "The artwork about to be uploaded." });
export type CreateArtworkUploadRequest = z.infer<typeof CreateArtworkUploadRequest>;

export const CreateArtworkUploadResponse = z
  .strictObject({
    uploadId: UploadId,
    upload: z.strictObject({
      url: z.url().meta({ description: "A presigned storage URL. PUT the file's bytes to it." }),
      method: z.literal("PUT"),
      headers: z.strictObject({ "Content-Type": z.enum(ARTWORK_UPLOAD_CONTENT_TYPES) }),
      expiresAt: Timestamp,
    }),
  })
  .meta({ id: "CreateArtworkUploadResponse" });
export type CreateArtworkUploadResponse = z.infer<typeof CreateArtworkUploadResponse>;

export const StoreArtworkRequest = z
  .strictObject({ uploadId: UploadId, kind: ArtworkKind })
  .meta({ id: "StoreArtworkRequest", description: "An uploaded file, and what it is for." });
export type StoreArtworkRequest = z.infer<typeof StoreArtworkRequest>;

export const StoredArtwork = z
  .strictObject({
    sha256: Sha256.meta({ description: "The original's SHA-256, which names it from now on." }),
    kind: ArtworkKind,
    tint: ArtworkTint.nullable().meta({
      description: "The colour a cover or landing is mostly made of; null for a map.",
    }),
  })
  .meta({ id: "StoredArtwork", description: "Artwork stored and processed for its kind." });
export type StoredArtwork = z.infer<typeof StoredArtwork>;

export const DraftArtworkParams = DraftParams.extend({
  kind: z.enum(["cover", "landing"]).meta({ description: "Which of the draft's artwork." }),
}).meta({ id: "DraftArtworkParams" });
export type DraftArtworkParams = z.infer<typeof DraftArtworkParams>;

export const SetDraftArtworkRequest = z
  .strictObject({
    sha256: Sha256.nullable().meta({
      description: "Artwork stored for this kind, or null to clear it.",
    }),
  })
  .meta({ id: "SetDraftArtworkRequest" });
export type SetDraftArtworkRequest = z.infer<typeof SetDraftArtworkRequest>;

const imageRefusals = {
  UNPROCESSABLE_CONTENT: {
    message: "The file can't be used as this artwork.",
    data: z.strictObject({ reason: z.string() }),
  },
  BAD_GATEWAY: { message: "Processing is unavailable right now; upload again and retry." },
} as const;

export const createArtworkUpload = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/presets/v1alpha1/artwork/uploads",
      operationId: "presets.artwork.uploads.create",
      summary: "Start uploading preset artwork.",
      description: "Returns a presigned URL to upload one file to, before storing it as artwork.",
      inputStructure: "detailed",
      successStatus: 201,
    }),
  )
  .errors({ ...errors.badRequest, ...teamRefusals, ...surfaceBanned })
  .input(z.object({ body: CreateArtworkUploadRequest }))
  .output(CreateArtworkUploadResponse);

export const storeArtwork = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/presets/v1alpha1/artwork",
      operationId: "presets.artwork.create",
      summary: "Store preset artwork.",
      description:
        "Scans an upload at the gate and makes its files for its kind: a cover, a landing poster and its video, or a map still. A draft then names it by its SHA-256.",
      inputStructure: "detailed",
      successStatus: 201,
    }),
  )
  .errors({
    ...errors.badRequest,
    ...teamRefusals,
    ...surfaceBanned,
    ...automodUnavailable,
    NOT_FOUND: { message: "Nothing was uploaded under that id." },
    ...imageRefusals,
  })
  .input(z.object({ body: StoreArtworkRequest }))
  .output(StoredArtwork);

export const setDraftArtwork = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/presets/v1alpha1/drafts/{draftId}/artwork/{kind}",
      operationId: "presets.drafts.artwork.set",
      summary: "Set a draft's cover or landing.",
      description:
        "Names artwork stored for this kind, or clears it. Takes `editPage`, and is refused while the draft is being published.",
      inputStructure: "detailed",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...teamRefusals,
    NOT_FOUND: { message: "The draft doesn't exist, or you aren't on its preset's team." },
    CONFLICT: { message: "The draft is being published." },
  })
  .input(z.object({ params: DraftArtworkParams, body: SetDraftArtworkRequest }))
  .output(z.strictObject({}));
